import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const ROLE_HIERARCHY = ['superadmin', 'admin', 'editor', 'viewer', 'attendance', 'roster', 'transportation', 'therapist'];

const ATTENDANCE_ALLOWED_PATHS = ['/attendance', '/mfa/setup', '/mfa/verify'];
const ROSTER_ALLOWED_PATHS = ['/classrooms/roster', '/attendance', '/', '/mfa/setup', '/mfa/verify'];
const TRANSPORTATION_ALLOWED_PATHS = ['/transportation', '/mfa/setup', '/mfa/verify'];
const TEACHER_ALLOWED_PATHS = ['/teacher', '/mfa/setup', '/mfa/verify'];
const THERAPIST_ALLOWED_PATHS = ['/therapy', '/mfa/setup', '/mfa/verify'];
const FINANCE_ALLOWED_PATHS = ['/', '/payroll', '/reports/enrollment', '/reports/revenue', '/mfa/setup', '/mfa/verify'];
const EXECUTIVE_ALLOWED_PATHS = ['/', '/reports/enrollment', '/reports/revenue', '/mfa/setup', '/mfa/verify'];

function pathOnAllowList(pathname, allowed) {
  return allowed.some((prefix) => {
    if (prefix === '/') return pathname === '/';
    return pathname === prefix || pathname.startsWith(`${prefix}/`);
  });
}

const PrivateRoute = ({ children, requiredRole, requiredRoles, skipMfaCheck }) => {
  const { user, loading, mfaRequired } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (!skipMfaCheck && mfaRequired) {
    return <Navigate to="/mfa/verify" />;
  }

  if (user.role === 'attendance') {
    const isAllowed = ATTENDANCE_ALLOWED_PATHS.some(p => location.pathname.startsWith(p));
    if (!isAllowed) {
      return <Navigate to="/attendance" />;
    }
  }

  if (user.role === 'roster') {
    const path = location.pathname;
    const isAllowed = ROSTER_ALLOWED_PATHS.some(
      (p) => path === p || path.startsWith(`${p}/`) || (p !== '/' && path.startsWith(p)),
    );
    if (!isAllowed) {
      return <Navigate to="/classrooms/roster" replace />;
    }
  }

  if (user.role === 'transportation') {
    const isAllowed = TRANSPORTATION_ALLOWED_PATHS.some(p => location.pathname.startsWith(p));
    if (!isAllowed) {
      return <Navigate to="/transportation" />;
    }
  }

  if (user.role === 'finance') {
    if (!pathOnAllowList(location.pathname, FINANCE_ALLOWED_PATHS)) {
      return <Navigate to="/payroll" replace />;
    }
  }

  if (user.role === 'executive') {
    if (!pathOnAllowList(location.pathname, EXECUTIVE_ALLOWED_PATHS)) {
      return <Navigate to="/" replace />;
    }
  }

  if (user.role === 'teacher') {
    const isAllowed = TEACHER_ALLOWED_PATHS.some(
      (p) => location.pathname === p || location.pathname.startsWith(`${p}/`)
    );
    if (!isAllowed) {
      return <Navigate to="/teacher/attendance" replace />;
    }
  }

  if (user.role === 'nurse') {
    const path = location.pathname;
    const studentChart = path === '/students' || /^\/students\/\d+/.test(path);
    const nurseReport = path === '/reports' || path === '/reports/medical-due' || path === '/reports/nurse-medications' || path === '/reports/physician-list';
    const mfa = path.startsWith('/mfa/');
    if (!(path === '/' || studentChart || nurseReport || mfa)) {
      return <Navigate to="/reports/medical-due" replace />;
    }
  }

  if (user.role === 'therapist') {
    const isAllowed = THERAPIST_ALLOWED_PATHS.some(
      (p) => location.pathname === p || location.pathname.startsWith(`${p}/`)
    );
    if (!isAllowed) {
      return <Navigate to="/therapy/schedule" replace />;
    }
  }

  if (requiredRoles && requiredRoles.length > 0) {
    if (!requiredRoles.includes(user.role)) {
      return <Navigate to="/" />;
    }
  } else if (requiredRole) {
    const requiredIdx = ROLE_HIERARCHY.indexOf(requiredRole);
    const userIdx = ROLE_HIERARCHY.indexOf(user.role);
    if (userIdx < 0 || userIdx > requiredIdx) {
      return <Navigate to="/" />;
    }
  }

  return children;
};

export default PrivateRoute;
